import { ChevronLeft, ChevronRight, Search, X } from "lucide-react"
import { cn } from "lib/utils"
import { Button } from "components/ui/button"
import { Input } from "components/ui/input"
import { AgendaFilters } from "./AgendaFilters"
import type { ViewMode, FilterState } from "../../types/agenda"
import { VIEW_LABELS } from "../../constants/agenda"

export type { ViewMode }

interface CalendarToolbarProps {
  viewMode: ViewMode
  onViewModeChange: (mode: ViewMode) => void
  currentDate: Date
  onDateChange: (date: Date) => void
  searchQuery: string
  onSearchChange: (query: string) => void
  filters: FilterState
  onFiltersChange: (filters: FilterState) => void
}

export function CalendarToolbar({
  viewMode,
  onViewModeChange,
  currentDate,
  onDateChange,
  searchQuery,
  onSearchChange,
  filters,
  onFiltersChange,
}: CalendarToolbarProps) {
  const navigateDate = (direction: "prev" | "next") => {
    const newDate = new Date(currentDate)
    if (viewMode === "Month") {
      newDate.setDate(1)
      newDate.setMonth(newDate.getMonth() + (direction === "next" ? 1 : -1))
    } else if (viewMode === "Week") {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 7 : -7))
    } else {
      newDate.setDate(newDate.getDate() + (direction === "next" ? 1 : -1))
    }
    onDateChange(newDate)
  }

  const goToToday = () => {
    onDateChange(new Date())
  }

  const getDateLabel = () => {
    const months = [
      "gennaio",
      "febbraio",
      "marzo",
      "aprile",
      "maggio",
      "giugno",
      "luglio",
      "agosto",
      "settembre",
      "ottobre",
      "novembre",
      "dicembre",
    ]
    const days = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"]

    if (viewMode === "Month") {
      return `${months[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    } else if (viewMode === "Week") {
      const weekStart = new Date(currentDate)
      weekStart.setDate(currentDate.getDate() + (currentDate.getDay() === 0 ? -6 : 1 - currentDate.getDay()))
      const weekEnd = new Date(weekStart)
      weekEnd.setDate(weekStart.getDate() + 6)

      const startDay = weekStart.getDate()
      const endDay = weekEnd.getDate()
      const startMonth = months[weekStart.getMonth()].substring(0, 3)
      const endMonth = months[weekEnd.getMonth()].substring(0, 3)
      const year = weekEnd.getFullYear()

      if (weekStart.getMonth() === weekEnd.getMonth()) {
        return `${startDay} - ${endDay} ${startMonth} ${year}`
      }
      return `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`
    } else {
      return `${days[currentDate.getDay()]} ${currentDate.getDate()} ${months[currentDate.getMonth()]}`
    }
  }

  return (
    <div className="agenda-toolbar bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] hexa-card-shadow rounded-lg p-3 flex items-center justify-between gap-3 flex-wrap">
      {/* Navigation Section */}
      <div className="flex w-full min-w-0 flex-wrap items-center gap-2 sm:w-auto">
        <div className="flex items-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-md border border-[var(--hexa-border)] text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
            onClick={() => navigateDate("prev")}
            aria-label="Periodo precedente"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 rounded-md border border-[var(--hexa-border)] text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)] hover:bg-[var(--hexa-border)]"
            onClick={() => navigateDate("next")}
            aria-label="Periodo successivo"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        <div aria-live="polite" className="calendar-date-label min-w-0 break-words text-[var(--hexa-text-primary)] font-medium text-sm">{getDateLabel()}</div>
        <Button variant="ghost" className="shrink-0 text-[var(--hexa-text-primary)]" onClick={goToToday}>Oggi</Button>
      </div>

      {/* Search and Filters Section */}
      <div className="flex min-w-0 w-full sm:w-auto items-center gap-2">
        <div className="relative min-w-0 flex-1 sm:w-[200px]">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--hexa-text-muted)]" />
          <Input
            type="text"
            placeholder="Cerca..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
            aria-label="Cerca eventi"
            className="h-10 pl-10 pr-10 bg-[var(--hexa-surface-elevated)] border border-[var(--hexa-border)] text-[var(--hexa-text-primary)] placeholder:text-[var(--hexa-text-muted)] rounded-lg w-full"
          />
          {searchQuery && <button type="button" aria-label="Azzera ricerca eventi" onClick={() => onSearchChange("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--hexa-text-muted)]"><X className="h-4 w-4" /></button>}
        </div>

        <AgendaFilters filters={filters} onFiltersChange={onFiltersChange} />
      </div>

      {/* View Switcher */}
      <div className="flex items-center bg-[var(--hexa-card-bg)] border border-[var(--hexa-border)] rounded-lg p-0.5">
        {(["Month", "Week", "Day"] as ViewMode[]).map((mode) => (
          <button
            key={mode}
            onClick={() => onViewModeChange(mode)}
            aria-pressed={viewMode === mode}
            className={cn(
              "px-3 py-1 text-xs font-medium rounded-md transition-all",
              viewMode === mode ? "bg-[var(--hexa-purple)] text-white" : "text-[var(--hexa-text-secondary)] hover:text-[var(--hexa-text-primary)]",
            )}
          >
            {VIEW_LABELS[mode]}
          </button>
        ))}
      </div>
    </div>
  )
}
